import type { Balance } from './balance';
import { effectiveStat } from './matchup';
import type { Motion } from './motion';
import type { Rng } from './rng';
import type {
  Combatant,
  DefenseSlot,
  MatchupResult,
  OffenseSlot,
  PlayEvent,
  PlayResult,
  Spot,
  UnitId,
} from './types';

/**
 * The run game (slice 6b): a carrier with the ball and the defenders chasing him. Runs use all of
 * it; every catch hands to `carry` (a screen since slice 6c, every pass since 10l), so the sim has
 * one tackle chain (docs/design-plan.md, "The playbook floor").
 *
 * The carrier's way is a track: spots at times, a straight line at his speed between two. Each
 * free defender starts chasing at a time from a spot and meets the carrier where a straight chase
 * at his own speed first reaches him (an exact intercept per leg, no time steps). Tacklers meet him
 * in that order; a broken tackle costs the carrier `run.breakDelaySeconds` and the rest re-aim, the
 * beaten tackler among them after `tackles.missedTackleSeconds` (slice 10m), once the carrier is
 * out of his reach from the spot he was beaten on (slice 10n). What a chaser who reaches him while
 * he stands stopped does is `tackles.duringStop` (slice 10o, `DuringStop`).
 * Nothing here draws from the rng except the battles and a strip's recovery.
 */

/**
 * The during-stop rule (slice 10o): a chaser who reaches the carrier while he stands stopped by a
 * broken tackle (`run.breakDelaySeconds`) either tackles him at once, a fresh battle and a fresh
 * stacked stop ("chain", shipped); or waits on him, and at the instant he resumes everyone who
 * waited is one gang tackle, beaten whole if the carrier wins it ("pile"); or has him down where he
 * stands, no battle ("down").
 */
export type DuringStop = 'chain' | 'pile' | 'down';
const DURING_STOP: readonly DuringStop[] = ['chain', 'pile', 'down'];

/** balance.json `tackles.duringStop`, checked: a typo must not ship as a rule. */
export function duringStopOf(b: Balance): DuringStop {
  const v = b.tackles.duringStop;
  if (!(DURING_STOP as readonly string[]).includes(v)) {
    throw new Error(`tackles.duringStop "${v}" is not one of ${DURING_STOP.join(', ')}`);
  }
  return v as DuringStop;
}

/**
 * The form of the gang mod (slice 10q): how the men beyond the tackler enter the tackle battle's
 * defense side. "flat" (shipped): the tackler's side × (1 + bonus) when a helper is there. "averaged":
 * the defense side reads (the tackler's positional strength + the helper's) / 2 × (1 + bonus), both
 * through the stat map as they play (matchup.ts `bWith`); a spent attack keeps the games' formula.
 * "perMan": the tackler's side × (1 + bonus × min(n − 1, `tackles.gangHelpersMax`)), n the men in the
 * tackle. Under every form the battle is the carrier against the first tackler and HP moves between
 * those two only.
 */
export type GangForm = 'flat' | 'averaged' | 'perMan';
const GANG_FORMS: readonly GangForm[] = ['flat', 'averaged', 'perMan'];

/** balance.json `tackles.gangForm`, checked: a typo must not ship as a rule. */
export function gangFormOf(b: Balance): GangForm {
  const v = b.tackles.gangForm;
  if (!(GANG_FORMS as readonly string[]).includes(v)) {
    throw new Error(`tackles.gangForm "${v}" is not one of ${GANG_FORMS.join(', ')}`);
  }
  return v as GangForm;
}

const off = (s: OffenseSlot): UnitId => `off:${s}`;
const def = (s: DefenseSlot): UnitId => `def:${s}`;

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
const dist = (a: Spot, c: Spot) => Math.hypot(c[0] - a[0], c[1] - a[1]);
/**
 * A contact this close after the carry starts, or after the last broken tackle, is at that moment
 * (slice 10l): the chasers' spots carry float error (a trail spot one step back is 1 yd, the tackle
 * reach, give or take 1e-14 s), which must not decide whether a tackle falls forward. A float
 * tolerance, not a rule.
 */
const AT_ONCE_SECONDS = 1e-9;

/** A spot the carrier passes at a time. `hole` marks where he reaches the line on a run. */
export interface TrackKey {
  t: number;
  at: Spot;
  hole?: true;
}
export type CarryTrack = TrackKey[];

/** Yards a second at this Speed: `run.yardsPerSecond × (Spe / run.speReference)^run.speedExponent`. */
export function speedOf(c: Combatant, b: Balance): number {
  const r = b.run;
  return r.yardsPerSecond * Math.pow(effectiveStat(c, 'spe', b) / r.speReference, r.speedExponent);
}

/** Where the track puts the carrier at time t (before the first key: at it; after the last: there). */
export function posOn(track: CarryTrack, t: number): Spot {
  const first = track[0] as TrackKey;
  if (t <= first.t) return first.at;
  for (let i = 1; i < track.length; i++) {
    const k1 = track[i] as TrackKey;
    if (t > k1.t) continue;
    const k0 = track[i - 1] as TrackKey;
    if (k1.t === k0.t) return k1.at;
    const f = (t - k0.t) / (k1.t - k0.t);
    return [k0.at[0] + (k1.at[0] - k0.at[0]) * f, k0.at[1] + (k1.at[1] - k0.at[1]) * f];
  }
  return (track.at(-1) as TrackKey).at;
}

/** Waypoints run at a steady speed from a spot at a time, as track keys. */
export function keysAlong(
  from: Spot,
  t0: number,
  spots: readonly Spot[],
  speed: number,
): TrackKey[] {
  const out: TrackKey[] = [];
  let at = from;
  let t = t0;
  for (const p of spots) {
    t += dist(at, p) / speed;
    out.push({ t, at: p });
    at = p;
  }
  return out;
}

/**
 * The track with the carrier stopped at time t for `d` seconds (a broken tackle): every key after t
 * comes `d` later. The stop shows as two keys at the contact spot.
 */
export function delayed(track: CarryTrack, t: number, d: number): CarryTrack {
  const at = posOn(track, t);
  return [
    ...track.filter((k) => k.t < t),
    { t, at },
    { t: t + d, at },
    ...track.filter((k) => k.t > t).map((k) => ({ ...k, t: k.t + d })),
  ];
}

/**
 * The first time from `tFrom` on that a chaser leaving `p` at t0 at `speed` (reaching `reach`
 * yards) can touch the carrier on the track, or undefined if he never can before its end. Per leg
 * the condition |C(t) − p| ≤ speed·(t − t0) + reach is a quadratic in t, solved exactly.
 */
export function intercept(
  track: CarryTrack,
  tFrom: number,
  p: Spot,
  t0: number,
  speed: number,
  reach: number,
): number | undefined {
  for (let i = 1; i < track.length; i++) {
    const k0 = track[i - 1] as TrackKey;
    const k1 = track[i] as TrackKey;
    const lo = Math.max(k0.t, t0, tFrom);
    if (lo > k1.t) continue;
    const span = k1.t - k0.t;
    const V: Spot =
      span > 0 ? [(k1.at[0] - k0.at[0]) / span, (k1.at[1] - k0.at[1]) / span] : [0, 0];
    const D: Spot = [k0.at[0] - p[0], k0.at[1] - p[1]];
    const k = speed * (k0.t - t0) + reach;
    // f(s) = |D + V s|² − (speed·s + k)², with s = t − k0.t; the chaser is there when f ≤ 0.
    const a = V[0] * V[0] + V[1] * V[1] - speed * speed;
    const bq = 2 * (D[0] * V[0] + D[1] * V[1] - speed * k);
    const c = D[0] * D[0] + D[1] * D[1] - k * k;
    const s0 = lo - k0.t;
    const s1 = k1.t - k0.t;
    const f = (s: number) => a * s * s + bq * s + c;
    if (f(s0) <= 0) return k0.t + s0;
    let s: number | undefined;
    if (Math.abs(a) < 1e-12) {
      if (bq < 0) s = -c / bq;
    } else {
      const disc = bq * bq - 4 * a * c;
      if (disc >= 0) {
        const r = Math.sqrt(disc);
        const [r1, r2] = [(-bq - r) / (2 * a), (-bq + r) / (2 * a)].sort((x, y) => x - y) as [
          number,
          number,
        ];
        // a > 0: f ≤ 0 between the roots; a < 0: outside them, and f(s0) > 0 puts s0 between.
        s = a > 0 ? (s0 < r1 ? r1 : undefined) : r2;
      }
    }
    if (s !== undefined && s >= s0 && s <= s1) return k0.t + s;
  }
  return undefined;
}

/**
 * The first time from `tFrom` on that the carrier on the track is more than `reach` yards from
 * `p`, or undefined if he never is before its end (slice 10n: a beaten tackler sets off when the
 * carrier leaves his reach). Per leg |C(t) − p|² − reach² is a quadratic in t, solved exactly; a
 * carrier standing inside the reach (a stop) never leaves on that leg.
 */
export function leaveTime(
  track: CarryTrack,
  tFrom: number,
  p: Spot,
  reach: number,
): number | undefined {
  for (let i = 1; i < track.length; i++) {
    const k0 = track[i - 1] as TrackKey;
    const k1 = track[i] as TrackKey;
    const lo = Math.max(k0.t, tFrom);
    if (lo > k1.t) continue;
    const span = k1.t - k0.t;
    const V: Spot =
      span > 0 ? [(k1.at[0] - k0.at[0]) / span, (k1.at[1] - k0.at[1]) / span] : [0, 0];
    const D: Spot = [k0.at[0] - p[0], k0.at[1] - p[1]];
    const s0 = lo - k0.t;
    const s1 = k1.t - k0.t;
    const g = (s: number) => (D[0] + V[0] * s) ** 2 + (D[1] + V[1] * s) ** 2 - reach * reach;
    if (g(s0) > 0) return lo;
    const a = V[0] * V[0] + V[1] * V[1];
    if (a < 1e-12) continue;
    const bq = 2 * (D[0] * V[0] + D[1] * V[1]);
    const c = D[0] * D[0] + D[1] * D[1] - reach * reach;
    const disc = bq * bq - 4 * a * c;
    if (disc < 0) continue;
    // Inside at s0, so the larger root is where he gets out; a hair past it is "more than".
    const s = (-bq + Math.sqrt(disc)) / (2 * a) + AT_ONCE_SECONDS;
    if (s >= s0 && s <= s1) return k0.t + s;
  }
  return undefined;
}

/** A Speed-weighted pick among the nearest Pokémon to a loose ball. One rng draw. */
export function recoverBall<T extends { c: Combatant }>(
  pool: readonly T[],
  rng: Rng,
  b: Balance,
): T {
  let x = rng.next() * pool.reduce((sum, p) => sum + effectiveStat(p.c, 'spe', b), 0);
  let rec = pool[0] as T;
  for (const p of pool) {
    rec = p;
    x -= effectiveStat(p.c, 'spe', b);
    if (x < 0) break;
  }
  return rec;
}

/** A defender going for the carrier: from where and when, how fast, and until when he can meet him. */
export interface Chaser {
  slot: DefenseSlot;
  from: Spot;
  t0: number;
  speed: number;
  until?: number;
}

/** The chasers who can reach the carrier on his track from `now` on, earliest first. */
export function arrivalOrder<C extends Chaser>(
  track: CarryTrack,
  now: number,
  chasers: readonly C[],
  reach: number,
): { p: C; t: number }[] {
  return chasers
    .map((p) => ({ p, t: intercept(track, now, p.from, p.t0, p.speed, reach) }))
    .filter((x): x is { p: C; t: number } => x.t !== undefined && x.t <= (x.p.until ?? Infinity))
    .sort((x, y) => x.t - y.t);
}

/**
 * The gang tackle (slice 10k): the next defender to reach the carrier helps the tackler who met him
 * at t if he gets there within `tackles.gangWindowSeconds` of that contact. The tackle battle is
 * still the carrier against the first tackler, his side × (1 + `tackles.gangTackleBonus`); the
 * helper lends the mod and nothing else (no battle, no HP). `carry` asks it of its next arrival,
 * on a run and after a catch alike: the one place a helper is found.
 */
export function gangHelper<C>(
  next: { p: C; t: number } | undefined,
  t: number,
  b: Balance,
): C | undefined {
  return next !== undefined && next.t - t <= b.tackles.gangWindowSeconds ? next.p : undefined;
}

/**
 * The men counted beyond the tackler (slice 10q), in arrival order: under "flat" and "averaged" the
 * helper alone; under "perMan" the pile beyond the tackler, then the window helper if he is not in
 * it, up to `tackles.gangHelpersMax`. At gangHelpersMax 1 that is flat's helper: on a chain the next
 * arrival, on a pile of two or more its second man (who is the next arrival too).
 */
export function gangMen<C>(
  helper: C | undefined,
  pile: readonly C[],
  b: Balance,
  form: GangForm = gangFormOf(b),
): C[] {
  if (form !== 'perMan') return helper === undefined ? [] : [helper];
  const men = pile.slice(1);
  if (helper !== undefined && !men.includes(helper) && pile[0] !== helper) men.push(helper);
  return men.slice(0, Math.max(0, b.tackles.gangHelpersMax));
}

/**
 * The tackle side's mod from the men counted beyond the tackler: none, 1; "perMan" 1 + bonus a man;
 * "flat" and "averaged" 1 + bonus whatever the count.
 */
export function tackleMod(counted: number, b: Balance, form: GangForm = gangFormOf(b)): number {
  if (counted <= 0) return 1;
  return 1 + b.tackles.gangTackleBonus * (form === 'perMan' ? counted : 1);
}

/**
 * The 2x fumble (slice 10k): a gang tackle the tackler wins, not on a crit, at a margin of
 * `tackles.fumbleMarginWith2x` or more knocks the ball loose (0 turns it off). A crit is a strip,
 * as without a helper.
 */
export function fumblesWith2x(r: MatchupResult, helped: boolean, b: Balance): boolean {
  const m = b.tackles.fumbleMarginWith2x;
  return helped && r.winner === 'b' && r.crit === undefined && m > 0 && r.margin >= m;
}

/** What resolution shares with the run game. */
export interface CarryContext {
  b: Balance;
  rng: Rng;
  m: Motion;
  O: Record<OffenseSlot, Combatant>;
  D: Record<DefenseSlot, Combatant>;
  events: PlayEvent[];
  /** Line of scrimmage, in yards from the offense's goal line. */
  los: number;
  /**
   * `bMod` multiplies the defender's side (a gang tackle); `bWith` is a second defender whose
   * positional strength is averaged into it (slice 10q, the "averaged" form).
   */
  battle: (
    kind: 'block' | 'tackle',
    o: OffenseSlot,
    d: DefenseSlot,
    t: number,
    bMod?: number,
    bWith?: DefenseSlot,
  ) => MatchupResult;
  /** Interpolate a time by margin between its value at margin 1 and margin 3. */
  byMargin: (margin: number, at1: number, at3: number) => number;
}

/** A line block as the carrier reads it: where it is and when its defender gets free. */
export interface Block {
  blocker: OffenseSlot;
  defender: DefenseSlot;
  /** Lateral of the engagement, in field yards. */
  lateral: number;
  /** When the defender is free: shed time, hold end, or Infinity for a pancake. */
  release: number;
  /**
   * On a run, a defender winning the block (not on a crit, slice 6e) stands here in his gap until
   * he sheds, the blocker driven back `run.pushYards` by the margin.
   */
  standsAt?: Spot;
}

/** When and from where blockers climb: by default from the formation at the snap. */
export interface ClimbFrom {
  /** When the blockers leave for the second level. */
  start: number;
  /** No block is fought before this. */
  earliest: number;
  /** A blocker who cannot get to his man by then does not block him, and the man stays free. */
  latest?: (d: DefenseSlot) => number;
  blockerAt: (bl: OffenseSlot) => Spot;
  defenderAt: (d: DefenseSlot) => Spot;
  /**
   * The defender's own way to the carrier (`pursuitOf`). With it, the blocker meets him on it, and
   * one who cannot before it ends (the defender reaches the carrier first) does not block him.
   */
  pursuit?: (d: DefenseSlot) => CarryTrack;
}

/** How a run sends its spare blockers (slice 6d). */
export interface RunBlocking {
  /** The designed gap, on the line. */
  gap: Spot;
  /** No line block at the designed gap (`gapBlockOf`). */
  edge: boolean;
  /** Each blocker's man from the play, if it names one. */
  targets: Partial<Record<OffenseSlot, DefenseSlot>>;
}

/** On a run, who the spare blockers go for first: the box (linemen and linebackers), the safety, the corners. */
const BOX_TIER: Record<DefenseSlot, number> = {
  DL1: 0,
  DL2: 0,
  LB1: 0,
  LB2: 0,
  S: 1,
  CB1: 2,
  CB2: 2,
};

/** The line block at the designed gap: the nearest within `run.gapWidthYards`. None: an edge run. */
export function gapBlockOf(blocks: readonly Block[], gap: Spot, b: Balance): Block | undefined {
  const near = (x: Block) => Math.abs(x.lateral - gap[1]);
  return blocks
    .filter((x) => near(x) <= b.run.gapWidthYards)
    .reduce<Block | undefined>(
      (best, x) => (best === undefined || near(x) < near(best) ? x : best),
      undefined,
    );
}

/**
 * A defender's way to the carrier on a run: where he lined up until his read, then straight to
 * where he first meets him (`meets`; Infinity if he never does, and then he stays put).
 */
export function pursuitOf(
  track: CarryTrack,
  tFrom: number,
  from: Spot,
  read: number,
  speed: number,
  reach: number,
): { path: CarryTrack; meets: number } {
  const meets = intercept(track, tFrom, from, read, speed, reach) ?? Infinity;
  const end = meets === Infinity ? (track.at(-1) as TrackKey).t : meets;
  const to = meets === Infinity ? from : posOn(track, meets);
  return {
    path: [
      { t: 0, at: from },
      { t: read, at: from },
      { t: Math.max(end, read), at: to },
    ],
    meets,
  };
}

/**
 * Spare blockers climb to the second level, each in one `block` battle no earlier than
 * `from.earliest`. Who takes whom:
 * - on a run (`run`): a blocker the play gives a target takes him if he is not rushing; the other
 *   defenders, in box order (`BOX_TIER`) on an inside run or nearest the designed gap alone on an
 *   edge run, each get the nearest spare blocker left (slices 6c, 6d);
 * - on a pass, each blocker in blocking order takes the unclaimed defender nearest him. On a screen
 *   the blocker nearest the back's cover man walls him off first (`first`), and the line leads later
 *   (`from`: at `screen.holdSeconds`, from where everyone is then).
 * Where: on the defender's way to the carrier (`from.pursuit`), else where he stands when the
 * blocker gets there. An open-field block lasts `run.openField`, not a pass block's time.
 * Writes each defender's release time into `release`.
 */
export function secondLevel(
  ctx: CarryContext,
  spare: readonly OffenseSlot[],
  candidates: readonly DefenseSlot[],
  formation: Readonly<Record<UnitId, Spot>>,
  first: DefenseSlot | undefined,
  release: Map<DefenseSlot, number>,
  from: ClimbFrom = {
    start: 0,
    earliest: ctx.b.run.climbSeconds,
    blockerAt: (bl) => formation[off(bl)],
    defenderAt: (d) => formation[def(d)],
  },
  run?: RunBlocking,
): void {
  const { b, m, events } = ctx;
  const F = b.field;
  const O = b.run.openField;
  const open = [...candidates];
  const left = [...spare];
  const nearest = <X>(xs: readonly X[], at: (x: X) => Spot, to: Spot): number => {
    let best = 0;
    xs.forEach((x, i) => {
      if (dist(at(x), to) < dist(at(xs[best] as X), to)) best = i;
    });
    return best;
  };
  const pairs: [OffenseSlot, DefenseSlot][] = [];
  const take = (bl: OffenseSlot, d: DefenseSlot) => {
    pairs.push([bl, d]);
    left.splice(left.indexOf(bl), 1);
    open.splice(open.indexOf(d), 1);
  };
  if (first !== undefined && open.includes(first) && left.length > 0) {
    take(left[nearest(left, from.blockerAt, from.defenderAt(first))] as OffenseSlot, first);
  }
  if (run !== undefined) {
    for (const bl of [...left]) {
      const d = run.targets[bl];
      if (d !== undefined && open.includes(d)) take(bl, d);
    }
    const toGap = (d: DefenseSlot) => dist(from.defenderAt(d), run.gap);
    const order = [...open].sort(
      (x, y) => (run.edge ? 0 : BOX_TIER[x] - BOX_TIER[y]) || toGap(x) - toGap(y),
    );
    for (const d of order) {
      if (left.length === 0) break;
      take(left[nearest(left, from.blockerAt, from.defenderAt(d))] as OffenseSlot, d);
    }
  }
  for (const bl of run === undefined ? [...left] : []) {
    if (open.length === 0) break;
    take(bl, open[nearest(open, from.defenderAt, from.blockerAt(bl))] as DefenseSlot);
  }
  for (const [bl, d] of pairs) {
    const bAt = from.blockerAt(bl);
    const speed = speedOf(ctx.O[bl], b);
    const way = from.pursuit?.(d);
    let t0: number;
    let dAt: Spot;
    if (way !== undefined) {
      // He meets the defender on his way to the carrier, or not at all.
      const t = intercept(way, from.earliest, bAt, from.start, speed, F.engageGapYards);
      if (t === undefined || (from.latest !== undefined && t > from.latest(d))) continue;
      t0 = t;
      dAt = posOn(way, t);
      const read = (way[1] as TrackKey).t;
      if (t0 > read) m.goto(def(d), dAt, read, t0);
    } else {
      t0 = Math.max(
        from.earliest,
        from.start + (dist(bAt, from.defenderAt(d)) - F.engageGapYards) / speed,
      );
      if (from.latest !== undefined && t0 > from.latest(d)) continue;
      dAt = m.posAt(def(d), t0);
    }
    const mid: Spot = [dAt[0] - F.engageGapYards / 2, dAt[1]];
    m.goto(off(bl), [dAt[0] - F.engageGapYards, dAt[1]], from.start, t0);
    m.goto(def(d), dAt, t0, t0);
    const r = ctx.battle('block', bl, d, t0);
    events.push({ t: t0, type: 'battle', a: off(bl), b: def(d), matchup: r, at: mid });
    if (r.winner === 'b') {
      // Lost in the open field: he slips it in run.openField's shed time, not a pass rusher's.
      const t =
        t0 +
        (r.crit === 'b'
          ? 0
          : ctx.byMargin(r.margin, O.shedSecondsAtMargin1, O.shedSecondsAtMargin3));
      events.push({ t, type: 'shed', unit: def(d), blocker: off(bl) });
      release.set(d, t);
    } else {
      const pancake = r.crit === 'a';
      // Won: it only has to last until the carrier is by, run.openField's hold time.
      const until = t0 + ctx.byMargin(r.margin, O.holdSecondsAtMargin1, O.holdSecondsAtMargin3);
      events.push({ t: t0, type: 'hold', unit: off(bl), rusher: def(d), until, pancake });
      if (pancake) release.set(d, Infinity);
      else {
        events.push({ t: until, type: 'shed', unit: def(d), blocker: off(bl) });
        release.set(d, until);
      }
    }
  }
}

/**
 * The carrier's track on a designed run: his path to the designed gap, then downfield. At the
 * handoff he reads the line: the designed gap if no block is there or its block holds until he
 * gets there, else a cutback behind the block that held longest. A block its defender is winning
 * (`standsAt`) has given way by the handoff, whenever he sheds. An edge run (no block at the
 * designed gap) bends toward its sideline at `run.edgeDriftYardsPerYard`, then runs up it.
 */
export function runTrack(
  ctx: CarryContext,
  carrier: OffenseSlot,
  start: Spot,
  path: readonly Spot[],
  blocks: readonly Block[],
): { track: CarryTrack; blocker: OffenseSlot | null; cutback: boolean } {
  const { b } = ctx;
  const R = b.run;
  const speed = speedOf(ctx.O[carrier], b);
  const designed = keysAlong(start, 0, path, speed);
  const gap = (designed.at(-1) as TrackKey).at;
  const atGap = (designed.at(-1) as TrackKey).t;
  const near = (x: Block) => Math.abs(x.lateral - gap[1]);
  const gapBlock = gapBlockOf(blocks, gap, b);
  /** When the block gives way as he reads it. */
  const gives = (x: Block) =>
    x.standsAt === undefined ? x.release : Math.min(x.release, R.handoffSeconds);

  const full: TrackKey[] = [{ t: 0, at: start }];
  let hole: Spot;
  let chosen: Block | undefined = gapBlock;
  let cutback = false;
  if (gapBlock === undefined || gives(gapBlock) >= atGap) {
    full.push(...designed);
    hole = gap;
  } else {
    // The block that held longest; ties to the one nearest the designed gap.
    chosen = blocks.reduce((best, x) =>
      gives(x) > gives(best) || (gives(x) === gives(best) && near(x) < near(best)) ? x : best,
    );
    cutback = chosen !== gapBlock;
    if (!cutback) {
      full.push(...designed);
      hole = gap;
    } else {
      const h = R.handoffSeconds;
      const before = designed.filter((k) => k.t < h);
      const at = posOn([{ t: 0, at: start }, ...designed], h);
      const side = Math.sign(gap[1] - chosen.lateral) || 1;
      hole = [gap[0], chosen.lateral + (side * R.gapWidthYards) / 2];
      full.push(...before, { t: h, at }, ...keysAlong(at, h, [hole], speed));
    }
  }
  const holeKey = full.at(-1) as TrackKey;
  holeKey.hole = true;

  // Downfield: straight up, or toward the sideline first on an edge run.
  const half = b.field.widthYards / 2;
  const lane = half - R.sidelineYards / 2;
  const legs: Spot[] = [];
  if (gapBlock === undefined && !cutback && Math.abs(hole[1]) < lane) {
    const side = Math.sign(hole[1]) || 1;
    const run = (lane - Math.abs(hole[1])) / R.edgeDriftYardsPerYard;
    const x = Math.min(100, hole[0] + run);
    legs.push([x, hole[1] + side * (x - hole[0]) * R.edgeDriftYardsPerYard]);
  }
  const last = legs.at(-1) ?? hole;
  if (last[0] < 100) legs.push([100, last[1]]);
  full.push(...keysAlong(hole, holeKey.t, legs, speed));
  return { track: full, blocker: chosen?.blocker ?? null, cutback };
}

/** A defender who can chase: from when, and (read from the motion then) from where. */
export interface Pursuer {
  slot: DefenseSlot;
  t0: number;
  /** He does not chase: he stands here (in his gap, engaged) and meets a carrier who comes by. */
  at?: Spot;
  /** He can meet the carrier only until then (when he sheds and chases instead). */
  until?: number;
}

export interface CarryOutcome {
  /** Net yards from the line of scrimmage to the end spot. */
  yards: number;
  result: PlayResult;
  endT: number;
  outOfBounds: boolean;
  /** The track as the carrier ran it, broken-tackle stops included. */
  track: CarryTrack;
}

/**
 * The tackle chain behind a carrier with the ball from `tFrom` on: the earliest chaser meets him
 * first, in a `tackle` battle (carrier avg(Atk, Def) vs tackler Atk), with the gang bonus when the
 * next arrival is within the window (`gangHelper`). A tackler win is a tackle
 * (`tackles.fallForwardYards` on) or, on its crit or a 2x fumble, a loose ball; a carrier win breaks
 * the tackle. A beaten tackler gets up and rejoins the chase from the contact spot
 * `tackles.missedTackleSeconds` later at his own speed, an ordinary chaser from then on (slice 10m;
 * 0: he is out of the play); his first contact needs the carrier more than `run.tackleReachYards`
 * from that spot, so a carrier still stopped there is waited for, not fallen on (slice 10n).
 * A chaser who reaches the carrier during a stop follows `tackles.duringStop` (slice 10o): "chain",
 * a tackle at once and a stop stacked on the first; "pile", he waits, and at the resume the pile is
 * one gang tackle (the first to have arrived the tackler, the next within the window the helper),
 * every man in it beaten if the carrier wins; "down", the carrier is down where he stands, no
 * battle. Contact within `run.sidelineYards` of a sideline puts him out of
 * bounds, with no loose ball. Nobody reaching him before the goal line is a touchdown. Moves the
 * carrier and every chaser.
 */
export function carry(
  ctx: CarryContext,
  carrier: OffenseSlot,
  start: CarryTrack,
  tFrom: number,
  pursuers: readonly Pursuer[],
): CarryOutcome {
  const { b, m, events, O, D, los } = ctx;
  const R = b.run;
  const F = b.field;
  const half = F.widthYards / 2;
  const toGoal = 100 - los;
  let track = start;
  /** `rejoinAt`: a beaten tackler, up at that time at `from`, the spot he was beaten on. */
  type Chasing = Pursuer & { from: Spot; speed: number; rejoinAt?: number };
  let left: Chasing[] = pursuers.map((p) => ({
    ...p,
    from: p.at ?? m.posAt(def(p.slot), p.t0),
    speed: p.at === undefined ? speedOf(D[p.slot], b) : 0,
  }));
  const rule = duringStopOf(b);
  const form = gangFormOf(b);
  /** When the carrier resumes after the stop a broken tackle put him in (slice 10o). */
  let stopUntil: number | undefined;
  /** `deferred`: he reached the carrier during the stop and waits on him (the pile). */
  type Arrival = { p: Chasing; t: number; deferred?: true };
  const arrivals = (now: number): Arrival[] => {
    // The reach rule (slice 10n): a beaten tackler's first contact after he is up needs the carrier
    // more than his reach from the spot he was beaten on; he has to catch him, not fall on him. A
    // carrier still inside it when the delay is up is waited for, and he sets off as the carrier
    // leaves it (never, if the carry ends there). Read off the track as it is now.
    for (const p of left) {
      if (p.rejoinAt === undefined) continue;
      p.t0 = leaveTime(track, p.rejoinAt, p.from, R.tackleReachYards) ?? Infinity;
    }
    const order: Arrival[] = arrivalOrder(track, Math.max(now, tFrom), left, R.tackleReachYards);
    // The pile (slice 10o): whoever reaches the stopped carrier waits on him until he resumes, in
    // the order he got there.
    if (rule === 'pile' && stopUntil !== undefined) {
      for (const x of order) {
        if (x.t < stopUntil) {
          x.t = stopUntil;
          x.deferred = true;
        }
      }
    }
    return order;
  };

  /** The ball is spotted on a whole yard line from the line of scrimmage. */
  const whole = (x: number) => Math.min(100, los + Math.round(x - los));
  let now = tFrom;
  let end: Spot | undefined;
  let endT = (track.at(-1) as TrackKey).t;
  let result: PlayResult = 'gain';
  let outOfBounds = false;
  const met: { p: Chasing; t: number; at: Spot }[] = [];
  /** The helper of the contact that ended the carry, drawn beside the carrier at the whistle. */
  let helped: Chasing | undefined;
  for (;;) {
    const order = arrivals(now);
    const next = order[0];
    if (next === undefined) break;
    const { p } = next;
    const t = next.t - now <= AT_ONCE_SECONDS ? now : next.t;
    const at = posOn(track, t);
    // Down (slice 10o): met by another defender while he stands stopped by a broken tackle, he is
    // down where he stands, no battle, no HP; the whistle at the meeting.
    if (rule === 'down' && stopUntil !== undefined && t < stopUntil) {
      end = at;
      endT = t;
      met.push({ p, t, at });
      events.push({ t, type: 'tackle', unit: off(carrier), by: [def(p.slot)], at, stopped: true });
      break;
    }
    // Where a tackle or strip is spotted: a whole yard, if he has a moment on the track to get there
    // (not at the instant of the catch, nor of the tackle he just broke, where he stands).
    const moment = t > now || (now === tFrom && track.some((k) => k.t < t));
    const spotted = (x: number): Spot => (moment ? [whole(x), at[1]] : at);
    const helper = gangHelper(order[1], t, b);
    // The pile (slice 10o): everyone who waited through the stop is in this one gang tackle.
    const pile = next.deferred ? order.filter((x) => x.deferred).map((x) => x.p) : [];
    // The form (slice 10q): who is counted beyond the tackler, the mod they lend, and the man whose
    // strength is averaged in. The battle is still the carrier against the first tackler.
    const men = gangMen(helper, pile, b, form);
    const r = ctx.battle(
      'tackle',
      carrier,
      p.slot,
      t,
      tackleMod(men.length, b, form),
      form === 'averaged' && helper ? helper.slot : undefined,
    );
    events.push({
      t,
      type: 'battle',
      a: off(carrier),
      b: def(p.slot),
      matchup: r,
      at,
      ...(helper ? { gang: def(helper.slot) } : {}),
      ...(men.length > 1 ? { helpers: men.map((x) => def(x.slot)) } : {}),
      ...(next.deferred ? { pile: pile.map((x) => def(x.slot)) } : {}),
    });
    met.push({ p, t, at });
    const by = [def(p.slot), ...men.map((x) => def(x.slot))];
    if (Math.abs(at[1]) >= half - R.sidelineYards) {
      end = spotted(at[0]);
      endT = t;
      outOfBounds = true;
      helped = helper;
      events.push({ t, type: 'tackle', unit: off(carrier), by, at: end, outOfBounds: true });
      break;
    }
    if (r.winner === 'b') {
      endT = t;
      helped = helper;
      const twoX = fumblesWith2x(r, helper !== undefined, b);
      if (r.crit === 'b' || twoX) {
        // The helper is the next arrival, so he is in the pool either way.
        end = spotted(at[0]);
        const pool = [
          { unit: off(carrier), c: O[carrier], side: 'off' as const },
          { unit: def(p.slot), c: D[p.slot], side: 'def' as const },
          ...order
            .slice(1)
            .map((x) => ({ unit: def(x.p.slot), c: D[x.p.slot], side: 'def' as const })),
        ].slice(0, b.tackles.recoveryCandidates);
        const rec = recoverBall(pool, ctx.rng, b);
        events.push({
          t,
          type: 'fumble',
          unit: off(carrier),
          by: def(p.slot),
          ...(twoX && helper ? { with: def(helper.slot) } : {}),
          recoveredBy: rec.unit,
          at: end,
        });
        // A ball lost in his own end zone scores as a safety until defenses can return it.
        if (rec.side === 'def') result = end[0] <= 0 ? 'safety' : 'fumble';
      } else {
        // He falls forward, if he has a moment on the track to do it in.
        end = spotted(at[0] + b.tackles.fallForwardYards);
        events.push({ t, type: 'tackle', unit: off(carrier), by, at: end });
      }
      break;
    }
    // A broken pile beats every man who stood in it (slice 10o); nobody in it fights again this stop.
    const beaten = pile.length > 0 ? pile : [p];
    const names = beaten.map((x) => def(x.slot));
    for (const x of beaten) {
      events.push({
        t,
        type: 'broken-tackle',
        unit: off(carrier),
        by: def(x.slot),
        at,
        ...(pile.length > 0 ? { pile: names } : {}),
      });
      if (x !== p) met.push({ p: x, t, at });
    }
    // By slot: a gap-stander is in the chase twice (standing, then chasing once he sheds).
    left = left.filter((x) => !beaten.some((y) => y.slot === x.slot));
    // He gets up and chases again from where he was beaten (slice 10m), once the carrier is out
    // of his reach there (slice 10n, `arrivals`).
    const rejoin = b.tackles.missedTackleSeconds;
    if (rejoin > 0) {
      const t0 = t + rejoin;
      for (const x of beaten) {
        left.push({ slot: x.slot, t0, rejoinAt: t0, from: at, speed: speedOf(D[x.slot], b) });
      }
    }
    track = delayed(track, t, R.breakDelaySeconds);
    // A break inside the stop ("chain") stacks on it; a pile's is at the resume; "down" never breaks
    // one inside a stop.
    stopUntil = (stopUntil !== undefined && t <= stopUntil ? stopUntil : t) + R.breakDelaySeconds;
    now = t;
    endT = (track.at(-1) as TrackKey).t;
  }
  end ??= (track.at(-1) as TrackKey).at;

  // The carrier along his track to the whistle; the last stretch straight to where he went down.
  const u = off(carrier);
  const before = track.filter((k) => k.t < endT && k.t >= (track[0] as TrackKey).t);
  for (let i = 1; i < before.length; i++) {
    const k0 = before[i - 1] as TrackKey;
    const k1 = before[i] as TrackKey;
    m.goto(u, k1.at, k0.t, k1.t);
  }
  const lastT = (before.at(-1) ?? (track[0] as TrackKey)).t;
  if (endT > lastT) m.goto(u, end, lastT, endT);
  // Each tackler arrives beside him; the others close in until the whistle.
  for (const { p, t, at } of met) {
    if (t > p.t0) m.goto(def(p.slot), [at[0] + F.tackleGapYards, at[1]], p.t0, t);
  }
  // A gang tackle's helper arrives at his other side, toward the middle of the field.
  if (helped && endT > helped.t0) {
    const side = end[1] > 0 ? -1 : 1;
    m.goto(def(helped.slot), [end[0], end[1] + side * F.tackleGapYards], helped.t0, endT);
  }
  for (const p of left) {
    if (p.at !== undefined || met.some((x) => x.p === p) || endT <= p.t0) continue;
    if (p === helped) continue;
    const to: Spot = [
      p.from[0] + (end[0] - p.from[0]) * 0.8,
      p.from[1] + (end[1] - p.from[1]) * 0.8,
    ];
    m.goto(def(p.slot), to, p.t0, endT);
  }

  // The ball is spotted on a whole yard (a tackle at the instant of a broken tackle is where he stands).
  const yards = clamp(whole(end[0]) - los, -los, toGoal);
  if (result === 'gain') {
    if (end[0] >= 100) result = 'touchdown';
    else if (end[0] <= 0) result = 'safety';
  }
  return { yards: result === 'touchdown' ? toGoal : yards, result, endT, outOfBounds, track };
}
